import React from 'react';

/**
 * BarraSemaforo — Kcal quemadas/ingeridas visual
 *
 * La barra crece desde el centro hacia los extremos:
 *   calIn > burned (exceso)  → crece a la IZQUIERDA  → color rojo (#ff2d55)
 *   burned > calIn (déficit) → crece a la DERECHA    → color verde (#39FF14)
 *   calIn === burned         → barra de ancho 0, solo tick central amarillo
 *
 * Desplazamiento máximo: ±300 kcal = llega al borde lateral.
 * Color gradual desde amarillo (centro) hacia rojo o verde (extremo).
 * Mismo aspecto y textos en todas las ubicaciones — el componente es autónomo.
 *
 * Props:
 *   calIn  {number}  Calorías ingeridas
 *   burned {number}  Calorías quemadas
 *   style  {object}  Estilos extra opcionales para el contenedor externo
 */
export default function BarraSemaforo({ calIn, burned, style }) {
  const ci = calIn  || 0;
  const bu = burned || 0;
  const hasData = ci > 0 && bu > 0;
  const diff    = ci - bu; // >0 exceso (izq/rojo), <0 déficit (der/verde)

  // normalized: -1 = full izq (rojo), 0 = centro, +1 = full der (verde)
  const normalized = hasData ? Math.max(-1, Math.min(1, -diff / 300)) : 0;

  // Interpolación RGB gradual
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  const getColor = (n) => {
    // yellow=[249,199,79]  green=[57,255,20]  red=[255,45,85]
    if (n >= 0) return `rgb(${lerp(249, 57, n)}, ${lerp(199, 255, n)}, ${lerp(79, 20, n)})`;
    const s = -n;
    return `rgb(${lerp(249, 255, s)}, ${lerp(199, 45, s)}, ${lerp(79, 85, s)})`;
  };

  const barColor  = hasData ? getColor(normalized) : 'rgba(255,255,255,0.18)';
  const fillWidth = Math.abs(normalized) * 44; // 0–44% del ancho total
  const goesLeft  = normalized < 0;            // exceso → crece izquierda

  // Posición CSS del fill: arranca en el centro y crece hacia un lado
  const fillLeftPct = goesLeft ? (50 - fillWidth) : 50;

  // Gradiente: amarillo en el lado del centro, color puro en el extremo
  const fillGrad = goesLeft
    ? `linear-gradient(90deg, ${barColor}, rgba(249,199,79,0.7))`
    : `linear-gradient(90deg, rgba(249,199,79,0.7), ${barColor})`;

  // Etiquetas laterales
  const roundedDiff = Math.round(diff);
  let leftLabel = null, rightLabel = null;
  if (hasData) {
    if (roundedDiff === 0) {
      leftLabel = '0';
      rightLabel = '0';
    } else if (roundedDiff > 0) {
      leftLabel = `+${roundedDiff} kcal`; // exceso → izquierda
    } else {
      rightLabel = `${roundedDiff} kcal`; // déficit → derecha
    }
  }

  return (
    <div style={{ ...style }}>
      {/* Fila de etiquetas: [valor izq] [Kcal quemadas/ingeridas] [valor der] */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{
          minWidth: 64, fontSize: 10, fontWeight: 900, letterSpacing: 0.3,
          color: leftLabel !== null ? barColor : 'transparent',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>
          {leftLabel || ''}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
          Kcal quemadas/ingeridas
        </span>
        <span style={{
          minWidth: 64, fontSize: 10, fontWeight: 900, letterSpacing: 0.3,
          color: rightLabel !== null ? barColor : 'transparent',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          textAlign: 'right',
        }}>
          {rightLabel || ''}
        </span>
      </div>

      {/* Barra — contenedor relativo para el tick central */}
      <div style={{ position: 'relative' }}>
        {/* Track (mismo estilo que Progreso de Pasos) */}
        <div style={{
          background: 'rgba(0,0,0,0.6)',
          borderRadius: 6,
          height: 6,
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.8)',
          position: 'relative',
        }}>
          {/* Fill: crece desde el centro hacia un lado */}
          <div style={{
            position: 'absolute',
            left: `${fillLeftPct}%`,
            width: `${fillWidth}%`,
            height: '100%',
            borderRadius: 6,
            background: fillGrad,
            boxShadow: fillWidth > 0 ? `0 0 10px ${barColor}80` : 'none',
            transition: 'left 0.6s cubic-bezier(0.4,0,0.2,1), width 0.6s cubic-bezier(0.4,0,0.2,1), background 0.6s',
          }} />
        </div>

        {/* Tick central: referencia visual, ligeramente más alto que el track */}
        <div style={{
          position: 'absolute',
          left: '50%',
          top: -1,
          transform: 'translateX(-50%)',
          width: 1,
          height: 8,
          background: 'rgba(255,255,255,0.3)',
          pointerEvents: 'none',
        }} />
      </div>
    </div>
  );
}
